import React, { useRef, useEffect } from 'react';
// Fix: Use namespace import for react-router-dom to avoid missing exported member errors
import * as ReactRouterDOM from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '../../contexts/FavoritesContext';
import { useFavoriteProducts } from '../../hooks/useFavoriteProducts';
import { formatCurrency } from '../../utils/helpers';

const { useNavigate } = ReactRouterDOM as any;

interface FavoritesDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const FavoritesDropdown: React.FC<FavoritesDropdownProps> = ({ isOpen, onClose }) => {
  const { favorites, toggleFavorite } = useFavorites();
  const { products: favoriteProducts, loading, error: fetchError, refetch: loadFavoriteProducts } = useFavoriteProducts({
    enabled: isOpen,
    clearWhenDisabled: false,
  });
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Fermer si on clique dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleViewAll = () => {
      onClose();
      navigate('/services?view=favoris');
  };

  const handleRemove = (e: React.MouseEvent, productId: number) => {
      e.stopPropagation();
      toggleFavorite(productId);
  };

  return (
    <div ref={dropdownRef} className="absolute top-full z-50 mt-2 w-[min(100vw-2rem,20rem)] max-w-[calc(100vw-2rem)] min-w-0 origin-top-right rounded-lg bg-white shadow-soft ring-1 ring-black/[0.06] dark:bg-gray-800 dark:ring-white/10 ltr:right-0 rtl:left-0 rtl:origin-top-left">
      <div className="min-w-0 p-4">
        <h3 className="mb-3 break-words text-base font-medium tracking-tight text-gray-800 dark:text-gray-100">{t('favorites.title')}</h3>
        
        {favorites.length === 0 ? (
            <div className="text-center py-6">
                <p className="text-gray-500 dark:text-gray-400 text-sm">{t('favorites.empty')}</p>
            </div>
        ) : loading ? (
            <div className="py-4 text-center text-sm text-gray-500 dark:text-gray-400" role="status" aria-live="polite">
              {t('common.loading')}
            </div>
        ) : fetchError ? (
            <div className="space-y-3 py-2" role="alert">
              <p className="text-center text-sm text-red-700/90 dark:text-red-300/90">{t('favorites.load_error')}</p>
              <button
                type="button"
                onClick={() => void loadFavoriteProducts()}
                className="ui-focus-visible w-full rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
              >
                {t('favorites.retry')}
              </button>
            </div>
        ) : (
            <>
                <div className="max-h-60 overflow-y-auto space-y-3 mb-4">
                    {favoriteProducts.map((product) => (
                        <div key={product.id} className="flex items-stretch gap-0 border-b border-gray-100/90 pb-2 last:border-0 dark:border-gray-700/80">
                            <button
                                type="button"
                                onClick={() => {
                                  onClose();
                                  navigate(`/product/${product.id}`);
                                }}
                                className="flex flex-1 min-w-0 items-start gap-3 rounded-lg py-1 pe-1 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-500/80 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800"
                                aria-label={`${t('favorites.view_product', { defaultValue: 'Voir le produit' })} — ${t(`product_names.${product.nom}`, { defaultValue: product.nom })}`}
                            >
                                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 dark:border-gray-600">
                                    <img
                                        src={product.image_url || 'https://placehold.co/100x100'}
                                        alt=""
                                        className="h-full w-full object-cover object-center"
                                    />
                                </div>
                                <div className="flex-1 min-w-0 pt-0.5">
                                    <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                                        {t(`product_names.${product.nom}`, { defaultValue: product.nom })}
                                    </p>
                                    <p className="text-xs font-medium text-primary-600/95 dark:text-primary-400/95 ltr:font-sans rtl:font-sans">
                                        {formatCurrency(product.prix)}
                                    </p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleRemove(e, product.id)}
                                className="ui-focus-visible min-h-11 min-w-11 shrink-0 inline-flex touch-manipulation items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600/90 dark:hover:bg-gray-700/80 dark:hover:text-red-400/90"
                                title={t('favorites.remove')}
                                aria-label={t('favorites.remove')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414-1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="space-y-2">
                    <button 
                        type="button"
                        onClick={handleViewAll}
                        className="ui-focus-visible w-full break-words rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-400"
                    >
                        {t('favorites.view_all')}
                    </button>
                </div>
            </>
        )}
      </div>
    </div>
  );
};

export default FavoritesDropdown;